import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import { PromptUtils, type Prompt } from '@lib/models/Prompt';
import Stack from '@mui/material/Stack';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getConnectionStateColor } from '@lib/models/enums';
import { ConnectionState } from '@lib/models/connectionState';
import { useAiChatServices } from '../AiChatContext';
import { usePromptState } from '@lib/services/storageService';
import { useTheme } from '@mui/material/styles';


interface LastPromptsProps {
  onPromptSelected: (promptText: string, promptJson?: string) => void;
  forceExpanded?: number;
  cancelTimeout: () => void;
  children?: React.ReactNode;
}

const LastPrompts: React.FC<LastPromptsProps> = ({ onPromptSelected, forceExpanded = 0, cancelTimeout, children }) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const { storageService } = useAiChatServices();
  const { promptState } = usePromptState(storageService);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listItemRefs = useRef<(HTMLLIElement | null)[]>([]);

  const theme = useTheme();
  // Load prompts on component mount and when refreshTrigger changes
  useEffect(() => {
    loadPrompts();
  }, [promptState]);

  useEffect(() => {
    if (forceExpanded > 0) {
      setIsExpanded(() => false);
    }
  }, [forceExpanded]);

  // ESC key listener to collapse prompt history
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsExpanded(() => false);
      }
    };

    const handleMouseUp = (_e: MouseEvent) => {
      setIsExpanded(() => false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const loadPrompts = () => {
    const savedPrompts = storageService.getPrompts();
    setPrompts(savedPrompts);
  };

  const handlePromptClick = (promptText: string, promptJson?: string) => {
    onPromptSelected(promptText, promptJson);
    setIsExpanded(() => false); // Collapse after selection
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    cancelTimeout();
  };

  // If no prompts, don't render anything
  if (prompts.length === 0) {
    return null;
  }

  const connectionColor = getConnectionStateColor(ConnectionState.Connected, theme);
  const borderColor = isExpanded ? connectionColor : theme.palette.divider;

  const lastPrompt = prompts.length > 0 ? prompts[0] : { promptText: 'No prompts yet' };
  const truncatedText = PromptUtils.truncateText(lastPrompt.promptText, 50, prompts.length);

  return (
    <Box
      sx={{
        mt: 0,
        borderTop: 0,
        borderLeft: `1px solid ${borderColor}`,
        borderRight: `1px solid ${borderColor}`,
        borderBottom: `1px solid ${borderColor}`,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        zIndex: "0",
        position: 'relative',
        top: '0px',
        marginLeft: '2.5%',
        overflow: 'hidden',
        width: '95%',
        bgcolor: 'background.default',
      }}
    >
      {/* Collapsed Tab */}
      <Box
        sx={{
          paddingTop: 0.5,
          paddingBottom: 0.5,
          paddingLeft: 1,
          paddingRight: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          borderTop: isExpanded ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.05)'
          }
        }}
        onClick={toggleExpanded}
      >

        <Box sx={{ overflow: 'clip', width: '50%', textWrap: 'nowrap', borderBottom: 'none' }}>
          <Stack direction="row" spacing={0}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem'
              }}
            >
              {!isExpanded && `${truncatedText}`}
              {isExpanded && `Last ${prompts.length} prompt${prompts.length > 1 ? 's' : ''}`}
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ width: '47%', textAlign: 'right' }}>
          {children}
        </Box>

        <Box sx={{ width: '3%', minWidth: '15px', textAlign: 'right' }}>
          {isExpanded ?
            <ChevronLeft sx={{ fontSize: '12px', transform: 'rotate(90deg)', marginLeft: '0.5rem', border: '1px solid rgba(255, 255, 255, 0.1)' }} /> :
            <ChevronRight sx={{ fontSize: '12px', transform: 'rotate(90deg)', marginLeft: '0.5rem', border: '1px solid rgba(255, 255, 255, 0.1)' }} />}
        </Box>
      </Box>

      {/* Expanded List */}
      <Collapse in={isExpanded}>
        <Box
          ref={scrollContainerRef}
          sx={{
            maxHeight: '30vh', // Maximum 30% of viewport height
            overflowY: 'auto'
          }}
        >
          {/* Prompts List */}
          <List sx={{ p: 0 }}>
            {prompts.map((prompt, index) => (
              <ListItem
                key={index + prompt.id}
                ref={(el) => {
                  listItemRefs.current[index] = el;
                }}
                onClick={() => handlePromptClick(prompt.promptText, prompt.promptJson)}
                sx={{
                  py: 1,
                  px: 1,
                  cursor: 'pointer',
                  bgcolor: 'Background.dafault',
                  border: 'none',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  borderRadius: 0,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.03)'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, verticalAlign: 'middle' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          minWidth: '20px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: 1,
                          pr: 0.5,
                          pl: 0,
                          py: 0.2,
                          lineHeight: 1
                        }}
                      >
                        #{prompts.length - index}
                      </Typography>
                      <Box
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.3,
                          fontSize: '0.8rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 3, // Maximum 3 lines
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'pre-wrap',
                          flex: 1
                        }}
                      >
                        {prompt.promptText}
                        {/* <Box
                          component="div"
                          sx={{
                            height: 20,
                            ml: 1,
                            lineHeight: 1,
                            fontSize: '0.65rem',
                            color: 'text.secondary',
                            mt: 0.5,
                            padding: '0 8px',
                            borderRadius: '16px',
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'background.paper'
                          }}
                        >
                          {PromptUtils.formatTimeAgo(prompt.created.toISOString())}
                        </Box> */}
                      </Box>
                    </Box>
                  } />
              </ListItem>
            ))}
          </List>
        </Box>
      </Collapse>
    </Box>
  );
};

export default LastPrompts;